import React from "react";

import { Paper, PaperProps, useTheme } from "@mui/material";
import { styled } from "@mui/material/styles";
import { OverridableStringUnion } from "@mui/types";

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: "8px",
  boxShadow: "none",
  padding: "30px 40px 30px 40px",
  border: `1px solid ${theme.igBorders.core.default}`,
}));

/**
 * @see Paper
 */

export type IgPaperColor = OverridableStringUnion<
  | "primary"
  | "secondary"
  | "success"
  | "error"
  | "info"
  | "warning"
  | "inherit"
  | "accent"
>;

/** Interior / frame treatment for the `variant="ai"` surface. */
export type IgAiSurfaceTone = "colorful" | "plain";

export type IgPaperProps = PaperProps & {
  isClickable?: boolean;
  isSelected?: boolean;
  isDisabled?: boolean;
  backgroundColor?: IgPaperColor;
  hoverBackgroundColor?: IgPaperColor;
  selectedBackgroundColor?: IgPaperColor;
  color?: string;
  borderColor?: IgPaperColor;
  hoverBorderColor?: IgPaperColor;
  selectedBorderColor?: IgPaperColor;
  /**
   * `variant="ai"` interior: `"colorful"` (default) lays a faint iridescent wash
   * over a white base; `"plain"` leaves a plain white interior.
   */
  background?: IgAiSurfaceTone;
  /**
   * `variant="ai"` frame: `"colorful"` (default) paints the iridescent gradient
   * border; `"plain"` uses a plain neutral border.
   */
  border?: IgAiSurfaceTone;
};

export const IgPaper = (props: IgPaperProps) => {
  const {
    children,
    isClickable,
    isSelected,
    isDisabled,
    backgroundColor: backgroundColorProp,
    hoverBackgroundColor: hoverBackgroundColorProp,
    selectedBackgroundColor: selectedBackgroundColorProp,
    color,
    borderColor: borderColorProp,
    hoverBorderColor: hoverBorderColorProp,
    selectedBorderColor: selectedBorderColorProp,
    background = "plain",
    border = "plain",
    variant,
    sx,
    ...rest
  } = props;
  const theme = useTheme();

  // The "ai" surface is a self-contained MUI Paper variant (see theme.tsx) that
  // carries the layout (rounded, transparent border, clipped) and skips the
  // interactive machinery below. The iridescent frame + wash are applied here
  // so they can be toggled per-instance via the `border` / `background` props.
  if (variant === "ai") {
    const colorfulBorder = border === "colorful";
    const colorfulBackground = background === "colorful";

    return (
      <Paper
        variant="ai"
        elevation={0}
        sx={[
          colorfulBorder
            ? {
                // Iridescent gradient frame via the border-box trick: white fills
                // the padding-box, the gradient paints the (transparent) border.
                backgroundImage: `linear-gradient(#fff, #fff), ${theme.igAi.gradient.stroke}`,
                backgroundOrigin: "border-box",
                backgroundClip: "padding-box, border-box",
              }
            : {
                border: `1px solid ${theme.igBorders.core.default}`,
                backgroundColor: "#fff",
              },
          // Faint iridescent wash over the interior, multiply-blended onto white.
          colorfulBackground && {
            "&::before": {
              content: '""',
              position: "absolute",
              inset: 0,
              borderRadius: "inherit",
              background: theme.igAi.surface.subtle,
              mixBlendMode: "multiply",
              pointerEvents: "none",
            },
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...rest}
      >
        {children}
      </Paper>
    );
  }

  return (
    <StyledPaper
      sx={[
        {
          ...(isClickable && {
            backgroundColor:
              isSelected && !isDisabled
                ? theme.igBackgroundColors.interactive.neutral.active
                : isDisabled
                  ? backgroundColorProp &&
                    theme.igBackgroundColors.interactive[
                      backgroundColorProp === "primary"
                        ? "core"
                        : backgroundColorProp
                    ]
                    ? theme.igBackgroundColors.interactive[
                        backgroundColorProp === "primary"
                          ? "core"
                          : backgroundColorProp
                      ].disabled
                    : theme.igBackgroundColors.interactive.neutral.disabled
                  : backgroundColorProp &&
                      theme.igBackgroundColors.interactive[
                        backgroundColorProp === "primary"
                          ? "core"
                          : backgroundColorProp
                      ]
                    ? theme.igBackgroundColors.interactive[
                        backgroundColorProp === "primary"
                          ? "core"
                          : backgroundColorProp
                      ].default
                    : theme.igBackgroundColors.baseLightMid,
            "&:hover": {
              backgroundColor: isDisabled
                ? theme.igBackgroundColors.interactive.neutral.disabled
                : isSelected
                  ? theme.igBackgroundColors.interactive.neutral.active
                  : theme.igBackgroundColors.baseLightHigh,
              cursor: !isDisabled ? "pointer" : "default",
              border: `${isClickable && isSelected ? "2" : "1"}px solid ${
                isDisabled
                  ? hoverBorderColorProp &&
                    theme.igBorders[
                      hoverBorderColorProp === "primary"
                        ? "core"
                        : hoverBorderColorProp
                    ]
                    ? theme.igBorders[
                        hoverBorderColorProp === "primary"
                          ? "core"
                          : hoverBorderColorProp
                      ].disabled
                    : theme.igBorders.core.disabled
                  : isClickable
                    ? selectedBorderColorProp &&
                      theme.igBorders[
                        selectedBorderColorProp === "primary"
                          ? "core"
                          : selectedBorderColorProp
                      ]
                      ? theme.igBorders[
                          selectedBorderColorProp === "primary"
                            ? "core"
                            : selectedBorderColorProp
                        ].active
                      : theme.igBorders.core.active
                    : hoverBorderColorProp &&
                        theme.igBorders[
                          hoverBorderColorProp === "primary"
                            ? "core"
                            : hoverBorderColorProp
                        ]
                      ? theme.igBorders[
                          hoverBorderColorProp === "primary"
                            ? "core"
                            : hoverBorderColorProp
                        ].hover
                      : theme.igBorders.core.hover
              }`,
            },
          }),
          border: `${isSelected ? "2" : "1"}px solid ${
            isDisabled
              ? borderColorProp &&
                theme.igBorders[
                  borderColorProp === "primary" ? "core" : borderColorProp
                ]
                ? theme.igBorders[
                    borderColorProp === "primary" ? "core" : borderColorProp
                  ].disabled
                : theme.igBorders.core.disabled
              : isClickable && isSelected
                ? selectedBorderColorProp &&
                  theme.igBorders[
                    selectedBorderColorProp === "primary"
                      ? "core"
                      : selectedBorderColorProp
                  ]
                  ? theme.igBorders[
                      selectedBorderColorProp === "primary"
                        ? "core"
                        : selectedBorderColorProp
                    ].active
                  : theme.igBorders.core.active
                : borderColorProp &&
                    theme.igBorders[
                      borderColorProp === "primary" ? "core" : borderColorProp
                    ]
                  ? theme.igBorders[
                      borderColorProp === "primary" ? "core" : borderColorProp
                    ].default
                  : theme.igBorders.core.default
          }`,
        },
        // Spread array sx entry-by-entry — object-spreading an array would
        // silently drop every style the caller passed.
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      variant={variant ?? "outlined"}
      elevation={0}
      {...rest}
    >
      {children}
    </StyledPaper>
  );
};
